/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-step-intro.js — Intro step controller (Phase 2.5 rebuild)
 *
 * Doc 6 §11.1 step id `intro`. Doc 6 §12.1 worked-example pattern.
 *
 * Multistep PDP's first step. Single-size and multi-size variants per
 * mockups/multistep-step1-intro-{single,multi}-size-{desktop,mobile}.html.
 * Bundle PDP uses the separate `branding_strategy` step instead.
 *
 * Three sections in the rendered DOM:
 *   1. Product mini-card: image + title + colour/size meta + price.
 *      Multi-size variant adds a row of `.jrs-size-chip` items.
 *   2. Decision list: two `.jrs-decision-card` (yes/no) with icon,
 *      "Recommended" badge on yes, title, description, and per-card
 *      CTA arrow row.
 *   3. Trust line: "Setup fees are always free" with checkmark in a
 *      success-tinted box.
 *
 * Data sources (read at onEnter from `context`):
 *   context.productTitle, context.productImage, context.productColour,
 *   context.productPricing.{priceCents, priceFormatted, currencyCode},
 *   context.sizes = [{variantId, sizeLabel, quantity}, ...]
 *
 * The single-size variant renders when `context.sizes` has 0 or 1
 * entries (or is missing); multi-size renders when length >= 2.
 *
 * State path per Doc 6 §5.3:
 *   state.draft.intro_choice = 'yes' | 'no'
 */

window.JRS = window.JRS || {};
window.JRS.Step = window.JRS.Step || {};

(function (JRS) {
  'use strict';

  var STEP_ID = 'intro';

  var _mountEl = null;
  var _listeners = [];
  var _cachedContext = null;

  function onEnter(state, context) {
    _mountEl = context.mountEl;
    if (!_mountEl) return;

    _cachedContext = _resolvePageContext(context);

    JRS.CustomizerPopup.setReassurance(
      'We\'ll email a design proof before we make anything. Change it as often as you like.'
    );
    // Hide the optional desktop summary panel (DELTA 7). Phase 2 ships
    // every step with the panel hidden; Phase 5 polish item documents
    // which steps would benefit from showing it (running totals etc.).
    JRS.CustomizerPopupState.dispatch({ type: 'HIDE_SUMMARY' });

    var currentChoice = (state.draft && state.draft.intro_choice) || null;
    _render(currentChoice, _cachedContext);
    _bindInteractions();
  }

  function onExit(state, context) {
    _unbindListeners();
    _mountEl = null;
    _cachedContext = null;
  }

  function onValidate(state, context) {
    var choice = state.draft && state.draft.intro_choice;
    if (choice !== 'yes' && choice !== 'no') {
      return { valid: false, errors: { intro_choice: 'required' } };
    }
    return { valid: true };
  }

  // ---------------------------------------------------------------------
  // Page context resolution
  // ---------------------------------------------------------------------

  function _resolvePageContext(context) {
    // The shell's _buildStepContext doesn't yet thread PDP context fields
    // through; fall back to window.JRS_PDP_CONTEXT which the trigger
    // populates at click time.
    var pdpCtx = (window.JRS_PDP_CONTEXT && typeof window.JRS_PDP_CONTEXT === 'object')
      ? window.JRS_PDP_CONTEXT
      : {};
    return {
      productTitle:   pdpCtx.productTitle || (context && context.productTitle) || '',
      productType:    pdpCtx.productType || (context && context.productType) || '',
      productVendor:  pdpCtx.productVendor || (context && context.productVendor) || '',
      productImage:   pdpCtx.productImage || '',
      productColour:  pdpCtx.productColour || '',
      productPricing: pdpCtx.productPricing || null,
      sizes:          (pdpCtx.sizes && pdpCtx.sizes.length > 0) ? pdpCtx.sizes : []
    };
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  function _render(currentChoice, ctx) {
    var totalQty = _sumSizes(ctx.sizes);
    var isMultiSize = ctx.sizes.length > 1;

    // Fixed heading: no product name, no pluralisation, no quantity
    // agreement. The card descriptions below still vary by size count.
    var titleText = 'Would you like this customised with your logo?';

    _mountEl.innerHTML =
      '<div class="jrs-step jrs-step--intro" data-jrs-step="intro">' +
      '  <header class="jrs-step__header">' +
      '    <h2 class="jrs-step__title">' + _escape(titleText) + '</h2>' +
      '  </header>' +
      '  <section class="jrs-step__body">' +
      '    <div class="jrs-decision-list" role="group" aria-label="Branding choice">' +
             _renderDecisionCard({
               choice: 'yes',
               modifier: 'jrs-decision-card--yes',
               icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12 L10 18 L20 6"/></svg>',
               recommended: true,
               title: 'Add my logo',
               description: isMultiSize
                 ? 'One logo applied to all ' + totalQty + ' items.'
                 : 'Pick your positions, upload your logo, choose how it\'s applied.',
               ctaLabel: 'Add my logo',
               selected: currentChoice === 'yes'
             }) +
             _renderDecisionCard({
               choice: 'no',
               modifier: 'jrs-decision-card--no',
               icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6 L5 6 L7 18 L19 18 L21 8 L7 8"/><circle cx="9" cy="20" r="1.5"/><circle cx="17" cy="20" r="1.5"/></svg>',
               recommended: false,
               title: 'No logo for now',
               description: isMultiSize
                 ? 'Add all ' + totalQty + ' items plain. You can add a logo later.'
                 : 'Add it plain. You can add a logo later.',
               ctaLabel: 'Add to cart',
               showArrow: false,
               selected: currentChoice === 'no'
             }) +
      '    </div>' +
      '    <p class="jrs-trust-line">' +
      '      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M3 8 L7 12 L13 4"/></svg>' +
      '      <span>No commitment until you confirm. <strong>We never charge logo set-up fees.</strong></span>' +
      '    </p>' +
      '  </section>' +
      '</div>';
  }

  function _renderDecisionCard(opts) {
    var recommendedHtml = opts.recommended
      ? '<span class="jrs-decision-card__recommended">Recommended</span>'
      : '';
    return (
      '<button type="button"' +
      '        class="jrs-decision-card ' + _escape(opts.modifier) +
                (opts.selected ? ' jrs-decision-card--selected' : '') + '"' +
      '        data-jrs-action="intro-choice"' +
      '        data-jrs-value="' + _escape(opts.choice) + '">' +
      '  <div class="jrs-decision-card__head">' +
      '    <span class="jrs-decision-card__icon" aria-hidden="true">' + opts.icon + '</span>' +
           recommendedHtml +
      '  </div>' +
      '  <h3 class="jrs-decision-card__title">' + _escape(opts.title) + '</h3>' +
      '  <p class="jrs-decision-card__description">' + _escape(opts.description) + '</p>' +
      '  <span class="jrs-decision-card__cta">' +
             _escape(opts.ctaLabel) +
             // Trailing arrow is opt-out: the "no logo" card renders its label
             // bare so the forward-motion cue sits only on the branding path.
             (opts.showArrow === false ? '' :
      '    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '      <path d="M5 12 L19 12 M13 6 L19 12 L13 18"/>' +
      '    </svg>') +
      '  </span>' +
      '</button>'
    );
  }

  // ---------------------------------------------------------------------
  // Interactions
  // ---------------------------------------------------------------------

  function _bindInteractions() {
    if (!_mountEl) return;
    var buttons = _mountEl.querySelectorAll('[data-jrs-action="intro-choice"]');
    for (var i = 0; i < buttons.length; i++) {
      _bind(buttons[i], 'click', _onChoiceClick);
    }
  }

  function _onChoiceClick(e) {
    var btn = e.currentTarget;
    var value = btn && btn.getAttribute('data-jrs-value');
    if (value !== 'yes' && value !== 'no') return;

    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'intro_choice', value: value }
    });

    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
      step_id: STEP_ID,
      decision_type: 'intro_choice',
      decision_value: value
    });

    if (value === 'no') {
      _handleSkipBranding(btn);
      return;
    }

    JRS.CustomizerPopup.goToStep('position_picker', { trigger: 'primary_cta' });
  }

  /**
   * Hamza decision 2026-05-20: "No logo for now" adds the product
   * to cart UNBRANDED (no branding line items, just the base variant(s))
   * and then closes the popup. Previous behaviour dropped the customer's
   * add-to-cart intent on close.
   *
   * 2026-05-20 Bug 1.1 follow-up — multi-line cart payload now matches
   * the customer's actual size grid selections. Previously a single
   * base-variant POST with summed quantity was sent regardless of which
   * sizes the customer picked. The data is already populated into
   * `ctx.sizes` by the trigger's `_readMultistepSizes` at click time
   * (jrs-v2-pdp-trigger.liquid:558); the intro controller just had to
   * use the per-size entries instead of summing them.
   *
   * Edge case: when `ctx.sizes` is empty or all entries have qty <= 0
   * (single-variant product without a size grid, OR multi-size grid
   * with no quantities chosen yet), we fall back to a single line item
   * for the default variant + quantity 1. Doesn't POST an empty cart.
   */
  function _handleSkipBranding(originatingButton) {
    var ctx = _cachedContext || _resolvePageContext({});
    var pdpCtx = (window.JRS_PDP_CONTEXT && typeof window.JRS_PDP_CONTEXT === 'object')
      ? window.JRS_PDP_CONTEXT
      : {};
    var defaultVariantId = parseInt(pdpCtx.productVariantId, 10);

    // Build per-size items from ctx.sizes. Each entry already carries a
    // resolved variantId (the colour × size combination the customer
    // picked) and quantity. Skip entries without a valid variantId or
    // qty <= 0 — defensive against the reader returning a stale row.
    var items = [];
    var totalQty = 0;
    if (ctx.sizes && ctx.sizes.length > 0) {
      for (var i = 0; i < ctx.sizes.length; i++) {
        var s = ctx.sizes[i];
        var sVid = parseInt(s && s.variantId, 10);
        var sQty = parseInt(s && s.quantity, 10);
        if (!sVid || sVid <= 0) continue;
        if (!isFinite(sQty) || sQty <= 0) continue;
        items.push({ id: sVid, quantity: sQty });
        totalQty += sQty;
      }
    }

    // Fallback: no usable per-size selections → single line item with
    // the default variant + quantity 1. Covers (a) single-variant
    // products with no size grid and (b) multi-size grids where the
    // customer didn't pick any quantities before clicking Skip.
    if (items.length === 0) {
      if (!defaultVariantId || defaultVariantId <= 0) {
        _showInlineError(
          'Could not resolve the product variant. Please close the popup and use the page Add-to-cart button.'
        );
        JRS.CustomizerPopup.fireAnalytics('pdp_customizer_unbranded_add_failed', {
          step_id: STEP_ID,
          error_reason: 'missing_variant_id'
        });
        return;
      }
      items.push({ id: defaultVariantId, quantity: 1 });
      totalQty = 1;
    }

    _setSkipButtonLoading(originatingButton, true);
    _setPopupBusy(true); // full-sheet loading overlay — blocks other popup clicks during the add
    _clearInlineError();

    if (!JRS.CartAPI || typeof JRS.CartAPI.addItems !== 'function') {
      _setSkipButtonLoading(originatingButton, false);
      _showInlineError('Cart service unavailable. Please try again.');
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_unbranded_add_failed', {
        step_id: STEP_ID,
        error_reason: 'cart_api_unavailable'
      });
      return;
    }

    JRS.CartAPI.addItems({ items: items })
      .then(function (result) {
        if (result && result.ok) {
          // Phase 5 telemetry-additions list: pdp_customizer_unbranded_add
          // is a new event not yet in Doc 3 v0.2; documented in
          // phase-2-status-and-resume-notes.md alongside the four
          // login-nudge events flagged in the Phase 2 closeout.
          // Payload reflects multi-line shape now: items_added counts
          // line items, total_quantity sums across them.
          JRS.CustomizerPopup.fireAnalytics('pdp_customizer_unbranded_add', {
            step_id: STEP_ID,
            items_added: items.length,
            total_quantity: totalQty
          });
          _setPopupBusy(false); // clear the loading overlay before the success receipt paints
          // Run 3 (2026-09-03, revert 2026-09-02) — the router fires
          // post_add_complete; on the REDIRECT arm it takes over (converted
          // close, clear draft, navigate). On the modal arm we fall through
          // UNCHANGED to the original variantaug unbranded success receipt.
          if (JRS.PostAdd && typeof JRS.PostAdd.complete === 'function' &&
              JRS.PostAdd.complete({ path: 'popup_skip_add' }) === 'redirect') {
            JRS.CustomizerPopup.close({
              reason: 'intro_decline_added_to_cart', isConfirm: true, force: true
            });
            if (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.dispatch === 'function') {
              JRS.CustomizerPopupState.dispatch({ type: 'RESET' });
            }
            if (JRS.PdpSelectionStore && typeof JRS.PdpSelectionStore.clear === 'function') {
              try { JRS.PdpSelectionStore.clear(); } catch (eClr) {}
            }
            return;
          }
          // Show the SAME in-popup success receipt a branded add shows — with no
          // branding rows — so a "No logo for now" add still gets the
          // "Added to cart / View cart" thank-you instead of the popup closing
          // silently. Falls back to the plain terminal close if the success step
          // isn't available (defensive; same converted-close outcome as before).
          if (!_showUnbrandedSuccess(ctx, totalQty)) {
            JRS.CustomizerPopup.close({
              reason: 'intro_decline_added_to_cart',
              isConfirm: true,
              force: true
            });
          }
          return;
        }
        // Cart API returned ok:false — surface inline, allow retry.
        _setSkipButtonLoading(originatingButton, false);
        _setPopupBusy(false);
        var errMsg = (result && result.error) || 'Could not add to cart. Please try again.';
        _showInlineError(errMsg);
        JRS.CustomizerPopup.fireAnalytics('pdp_customizer_unbranded_add_failed', {
          step_id: STEP_ID,
          error_reason: 'cart_add_failed',
          error_message: errMsg,
          items_attempted: items.length
        });
      })
      .catch(function (err) {
        _setSkipButtonLoading(originatingButton, false);
        _setPopupBusy(false);
        var errMsg = (err && err.message) || 'Cart submission threw an unexpected error.';
        _showInlineError(errMsg);
        JRS.CustomizerPopup.fireAnalytics('pdp_customizer_unbranded_add_failed', {
          step_id: STEP_ID,
          error_reason: 'cart_add_threw',
          error_message: errMsg,
          items_attempted: items.length
        });
      });
  }

  /**
   * Route the unbranded skip-add to the shared terminal 'success' step — the same
   * thank-you receipt a branded add shows (product line + "Added to cart" + View
   * cart / Continue), but with an empty brandingLines array so no logo rows
   * render. Returns true if the success step took over; false if it's unavailable
   * (the caller then does the plain terminal close, as before).
   */
  function _showUnbrandedSuccess(ctx, totalQty) {
    var pop = JRS.CustomizerPopup;
    var success = window.JRS && JRS.CustomizerSuccessStep;
    if (!success || typeof success.prepare !== 'function' ||
        !pop || typeof pop.goToStep !== 'function') {
      return false;
    }
    var isMultiSize = !!(ctx && ctx.sizes && ctx.sizes.length > 1);
    var variant = isMultiSize ? 'multi_size_multistep' : 'single';
    var snapshot = {
      isBundle: false,
      unbranded: true,      // skip-add — success receipt omits the branding copy/proof line
      variant: variant,
      flow: null,
      product: _buildUnbrandedProduct(ctx, variant, totalQty),
      brandingLines: [],   // unbranded — no logo rows (render omits the branding section)
      cartMeta: null,       // the success step fills this via CartAPI.getCart()
      pattern: null
    };
    try {
      success.prepare(snapshot);
      pop.goToStep('success', { trigger: 'skip_add' });
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Minimal success-receipt product line for the unbranded add — mirrors the
   * review controller's _buildSnapshotProduct shape ({ title, imageUrl, metaLine,
   * priceLabel }) from the intro's page context. Multi-size shows "{n} items" and
   * defers the price to the success step's authoritative cart-meta row (rather
   * than a misleading per-unit figure); single-size shows the unit price.
   */
  function _buildUnbrandedProduct(ctx, variant, totalQty) {
    ctx = ctx || {};
    var sizes = ctx.sizes || [];
    var pricing = ctx.productPricing || null;
    var metaParts = [];
    if (ctx.productColour) metaParts.push(ctx.productColour);
    if (variant === 'multi_size_multistep') {
      metaParts.push(totalQty + ' items');
    } else {
      if (sizes.length === 1 && sizes[0] && sizes[0].sizeLabel) metaParts.push(sizes[0].sizeLabel);
      metaParts.push('Qty ' + totalQty);
    }
    var priceLabel = (variant !== 'multi_size_multistep' && pricing && pricing.priceFormatted)
      ? pricing.priceFormatted
      : '';
    return {
      title: ctx.productTitle || '',
      imageUrl: ctx.productImage || '',
      metaLine: metaParts.filter(Boolean).join(' · '),
      priceLabel: priceLabel
    };
  }

  function _setPopupBusy(isBusy) {
    var pop = window.JRS && JRS.CustomizerPopup;
    if (pop && typeof pop.setBusy === 'function') pop.setBusy(isBusy);
  }

  function _setSkipButtonLoading(btn, isLoading) {
    if (!btn) return;
    if (isLoading) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  }

  function _showInlineError(msg) {
    if (!_mountEl) return;
    var existing = _mountEl.querySelector('[data-jrs-intro-error]');
    if (existing) {
      existing.textContent = msg;
      existing.hidden = false;
      return;
    }
    // Inject an error region after the decision list. CSS for
    // .jrs-step__error already exists in jrs-v2-steps.css.
    var body = _mountEl.querySelector('.jrs-step__body');
    if (!body) return;
    var errEl = document.createElement('p');
    errEl.setAttribute('data-jrs-intro-error', '');
    errEl.className = 'jrs-step__error';
    errEl.setAttribute('role', 'alert');
    errEl.textContent = msg;
    body.appendChild(errEl);
  }

  function _clearInlineError() {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-intro-error]');
    if (err) { err.textContent = ''; err.hidden = true; }
  }

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  function _sumSizes(sizes) {
    var total = 0;
    for (var i = 0; i < sizes.length; i++) total += (sizes[i].quantity || 0);
    return total;
  }

  function _bind(el, evt, fn) {
    el.addEventListener(evt, fn);
    _listeners.push({ el: el, evt: evt, fn: fn });
  }

  function _unbindListeners() {
    for (var i = 0; i < _listeners.length; i++) {
      var t = _listeners[i];
      try { t.el.removeEventListener(t.evt, t.fn); } catch (e) {}
    }
    _listeners = [];
  }

  function _escape(s) {
    if (typeof s !== 'string') return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ---------------------------------------------------------------------
  // Public API + register
  // ---------------------------------------------------------------------

  JRS.Step.Intro = {
    STEP_ID: STEP_ID,
    onEnter: onEnter,
    onExit: onExit,
    onValidate: onValidate
  };

  function _register() {
    if (window.JRS && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.registerStep === 'function') {
      JRS.CustomizerPopup.registerStep(STEP_ID, {
        onEnter: onEnter,
        onExit: onExit,
        onValidate: onValidate
      });
    } else {
      window.JRS = window.JRS || {};
      window.JRS._pendingStepRegistrations = window.JRS._pendingStepRegistrations || [];
      window.JRS._pendingStepRegistrations.push({
        stepId: STEP_ID,
        definition: { onEnter: onEnter, onExit: onExit, onValidate: onValidate }
      });
    }
  }
  _register();
})(window.JRS);
